import { faObjectGroup } from "@fortawesome/pro-regular-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";
import { FIELD } from "../../../../../actions/Layers/fields";
import { checkQueryKey } from "../../../../../hooks/mutations/usePatchCheckMutation";
import { setRetrospectiveGroupMatchingSiblings } from "../../../../../offline/sessionManager";
import { toast } from "../../../../../utils/toast";
import { callAvosApi } from "../../../../../utils/useAvosApiHook";
import { Button } from "../../../../Helper/Button";
import GroupByModal, { useGroupBy } from "../../../../Helper/GroupByModal";

interface Candidate {
    layer_id: string;
    label: string;
    type: string;
    matches_group: boolean;
    already_attached: boolean;
    group_by: Record<string, string>;
    group_by_labels: { key: string; label: string }[];
    parent_types: string[];
}

interface RetrospectiveGroupingModalProps {
    tenantId: string;
    checkId: number;
    offlineSessionId?: string | null;
    layerTypeLabel: string;
    groupByFields: { value: string; label: string }[];
}

export default function RetrospectiveGroupingModal({ tenantId, checkId, offlineSessionId, layerTypeLabel, groupByFields }: RetrospectiveGroupingModalProps) {
    const queryClient = useQueryClient();
    const groupByProps = useGroupBy(groupByFields, null);
    const { groupByArray, getGroupByLabel } = groupByProps;
    const [open, setOpen] = useState(false);
    const [candidates, setCandidates] = useState<Candidate[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [offlineGroupMode, setOfflineGroupMode] = useState<"grouped" | "all">("all");
    const [loading, setLoading] = useState(false);

    const openModal = async () => {
        if (offlineSessionId) {
            setOfflineGroupMode(groupByArray.length ? "grouped" : "all");
            setOpen(true);
            return;
        }
        setLoading(true);
        try {
            const response = await callAvosApi(`/${tenantId}/checks/${checkId}/retrospective-group-candidates`);
            const loadedCandidates = response.data as Candidate[];
            setCandidates(loadedCandidates);
            setSelectedIds(loadedCandidates.filter((candidate) => candidate.already_attached).map((candidate) => candidate.layer_id));
            setOpen(true);
            if (!loadedCandidates.length) toast.info("No sibling layers are available for grouping.");
        } catch {
            // The API client provides the error toast.
        } finally {
            setLoading(false);
        }
    };

    const toggleCandidate = (layerId: string) => {
        const candidate = candidates.find((item) => item.layer_id === layerId);
        if (candidate?.already_attached) return;
        setSelectedIds((current) => {
            if (current.includes(layerId)) return current.filter((id) => id !== layerId);
            return [...current, layerId];
        });
    };

    const toggleGroup = (groupCandidates: Candidate[]) => {
        const selectableIds = groupCandidates.map((candidate) => candidate.layer_id);
        setSelectedIds((current) => {
            const allSelected = selectableIds.every((id) => current.includes(id));
            if (allSelected) {
                return current.filter((id) => (
                    !selectableIds.includes(id)
                    || groupCandidates.some((candidate) => candidate.layer_id === id && candidate.already_attached)
                ));
            }
            return Array.from(new Set([...current, ...selectableIds]));
        });
    };

    const visibleGroupByFields = groupByArray.map((key) => ({ key, label: getGroupByLabel(key) || key }));
    const offlineCopyDescription = offlineGroupMode === "grouped" && visibleGroupByFields.length
        ? `When submitted, these check results will be copied to all siblings (${layerTypeLabel}) with the same ${visibleGroupByFields.map((field) => field.label).join(", ")}.`
        : `When submitted, these check results will be copied to all siblings (${layerTypeLabel}) sharing the same parent.`;
    const groupedCandidates = candidates.reduce<Record<string, Candidate[]>>((groups, candidate) => {
        const groupKey = visibleGroupByFields.map((field) => candidate.group_by[field.key] ?? "-").join(" | ") || "All siblings";
        groups[groupKey] = [...(groups[groupKey] ?? []), candidate];
        return groups;
    }, {});

    const attach = async () => {
        if (offlineSessionId) {
            setLoading(true);
            try {
                const selectedGroupBy = offlineGroupMode === "grouped" ? groupByArray : [];
                await setRetrospectiveGroupMatchingSiblings(offlineSessionId, true, selectedGroupBy);
                toast.success("The selected sibling layers will be attached when this check syncs.");
                setOpen(false);
            } catch {
                toast.error("Could not save the grouping choice locally.");
            } finally {
                setLoading(false);
            }
            return;
        }
        if (!selectedIds.length) {
            toast.error("Select at least one layer.");
            return;
        }
        setLoading(true);
        try {
            const response = await callAvosApi(`/${tenantId}/checks/${checkId}/retrospective-group`, {
                method: "POST",
                data: { layer_ids: selectedIds },
            });
            const attached = (response.data as { attached_layer_ids: string[] }).attached_layer_ids;
            queryClient.setQueryData(checkQueryKey({ tenant_id: tenantId, check_id: checkId }), (current: any) => ({
                ...current,
                layer_ids: Array.from(new Set([...(current?.layer_ids ?? []), ...selectedIds])),
            }));
            toast.success(`Check attached to ${attached.length} layer${attached.length === 1 ? "" : "s"}.`);
            setOpen(false);
        } catch {
            // The API client provides the error toast.
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button color="secondary" outline disabled={loading} onClick={openModal} icon={faObjectGroup}>
                Copy QC
            </Button>
            <Modal isOpen={open} toggle={() => setOpen(false)} size="xl">
                <ModalHeader toggle={() => setOpen(false)}>Copy QC to selected {layerTypeLabel}</ModalHeader>
                <ModalBody>
                    <p className="text-muted mb-3">
                        Already linked {layerTypeLabel.toLowerCase()}s cannot be removed. To correct a mistake, invalidate the check and create a new one with the correct {layerTypeLabel.toLowerCase()}s.
                    </p>
                    {offlineSessionId && (
                        <>
                            <p>Copy QC to {layerTypeLabel.toLowerCase()}s sharing same:</p>
                            <div className="d-flex flex-column gap-2 mb-3">
                                <label className="d-flex align-items-center gap-2">
                                    <input
                                        type="radio"
                                        name="offline-group-mode"
                                        className="form-check-input mt-0"
                                        checked={offlineGroupMode === "all"}
                                        onChange={() => setOfflineGroupMode("all")}
                                    />
                                    All siblings
                                </label>
                                <label className="d-flex align-items-center gap-2">
                                    <input
                                        type="radio"
                                        name="offline-group-mode"
                                        className="form-check-input mt-0"
                                        checked={offlineGroupMode === "grouped"}
                                        onChange={() => setOfflineGroupMode("grouped")}
                                    />
                                    Sharing the selected fields
                                </label>
                                {offlineGroupMode === "grouped" && (
                                    <GroupByModal
                                        title="Select grouping fields"
                                        description="Select one or more configured fields to match when copying this QC offline."
                                        groupByProps={groupByProps}
                                    />
                                )}
                            </div>
                            <span>{offlineCopyDescription}</span>
                        </>
                    )}
                    {!offlineSessionId && candidates.length > 0 && (
                        <>
                            <div className="d-flex justify-content-end mb-2">
                                <GroupByModal
                                    title="Group by"
                                    description="Group the layers by the following fields"
                                    groupByProps={groupByProps}
                                />
                            </div>
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th aria-label="Select" />
                                        <th>Layer</th>
                                        {visibleGroupByFields.map((field) => <th key={field.key}>{field.label}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(groupedCandidates).map(([groupKey, groupCandidates]) => {
                                        const selectableCandidates = groupCandidates;
                                        const allSelected = selectableCandidates.length > 0
                                            && selectableCandidates.every((candidate) => selectedIds.includes(candidate.layer_id));
                                        return (
                                            <Fragment key={groupKey}>
                                                <tr key={`${groupKey}-header`} className="table-light">
                                                    <th>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input mt-0"
                                                            checked={allSelected}
                                                            disabled={!selectableCandidates.length}
                                                            onChange={() => toggleGroup(groupCandidates)}
                                                        />
                                                    </th>
                                                    <th colSpan={visibleGroupByFields.length + 1}>{groupKey}</th>
                                                </tr>
                                                {groupCandidates.map((candidate) => {
                                                    const candidateLayer = { id: candidate.layer_id, label: candidate.label, ...candidate.group_by } as any;
                                                    const layerLabel = FIELD.LABEL_LINK.set_value({ layer: candidateLayer } as any).display_component();
                                                    return (
                                                        <tr key={candidate.layer_id} className={!candidate.matches_group ? "text-muted" : undefined}>
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input mt-0"
                                                                    checked={selectedIds.includes(candidate.layer_id)}
                                                                    disabled={candidate.already_attached}
                                                                    onChange={() => toggleCandidate(candidate.layer_id)}
                                                                />
                                                            </td>
                                                            <td>{layerLabel}</td>
                                                            {visibleGroupByFields.map((field) => {
                                                                const metaField = FIELD.CUSTOM_LAYER_META;
                                                                metaField.fieldname_getter = field.key;
                                                                return <td key={field.key}>{metaField.set_value({ layer: candidateLayer } as any).display_component()}</td>;
                                                            })}
                                                        </tr>
                                                    );
                                                })}
                                            </Fragment>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </>
                    )}
                    {!offlineSessionId && !candidates.length && <span>No sibling layers found.</span>}
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button color="primary" disabled={loading || (!offlineSessionId && !selectedIds.length)} onClick={attach}>Copy QC</Button>
                </ModalFooter>
            </Modal>
        </>
    );
}
